import { createContext, ReactNode, useContext, useMemo } from 'react'

/**
 * creates React Context with useContext wrapper and Extensible provider, which
 *  - receives context value as separate props
 *  - amends context value with passed props
 *  - memoizes context value (but not separate props)
 *
 * @param defaultValue - optional initial value, default - empty object
 *
 * @example
 * const [MyContextProvider, useMyContext] = createExtensibleContext<{
 *   a?: string
 *   b?: number
 *   c?: string
 * }>({ a: 'a0' })
 *
 * function RootComponent() {
 *   console.log(useMyContext()) // {a:'a0'}
 *
 *   return (
 *     <MyContextProvider a="a1" b={1}>
 *       <MiddleComponent />
 *     </MyContextProvider>
 *   )
 * }
 *
 * function MiddleComponent() {
 *   console.log(useMyContext()) // {a:'a1', b:1}
 *
 *   return (
 *     <MyContextProvider b={2} c="c2">
 *       <InnerComponent />
 *     </MyContextProvider>
 *   )
 * }
 *
 * function InnerComponent() {
 *   console.log(useMyContext()) // {a:'a1', b:2, c:'c2'}
 *
 *   return null
 * } */
export const createExtensibleContext = <T extends Record<string, unknown>>(
  defaultValue: T = {} as never,
) => {
  const extensibleContext = createContext<T>(defaultValue)

  const ExtensibleProvider = ({ children, ...props }: T & { children: ReactNode }) => {
    const context = useContext(extensibleContext)
    // amend current context by new values
    const merged = { ...context, ...props } as T
    const value = useMemo(
      () => merged,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [...Object.values(merged)], // compares merged values for changes one by one
    )

    return <extensibleContext.Provider value={value}>{children}</extensibleContext.Provider>
  }

  const useExtensibleContext = () => useContext(extensibleContext)

  return [ExtensibleProvider, useExtensibleContext] as const
}
